import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Clock, FlaskConical, Package } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { CLUSTERS, type ClusterKey } from "@/lib/clusters";
import { listCmsPosts } from "@/lib/blog-cms.functions";
import type { PublicPost } from "@/lib/blog/types";
import { useT } from "@/lib/i18n/provider";

type Post = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  date: string;
  readingTime: string;
  cluster: ClusterKey;
};

const POSTS: Post[] = [
  {
    slug: "diwali-healthy-gifts-ideas",
    title: "Diwali Healthy Gifts Ideas for Family, Friends & Colleagues",
    excerpt: "Thoughtful Diwali gifts people keep using after the festival, with clear notes on who each one suits.",
    category: "Gifting",
    date: "2026-10-05",
    readingTime: "10 min read",
    cluster: "festive",
  },
  {
    slug: "magnesium-for-migraines",
    title: "Magnesium for Migraines: Benefits, Best Forms & Dosage",
    excerpt: "Which forms have migraine trial evidence, how much was studied, when results may appear, and who should speak to a doctor first.",
    category: "Minerals",
    date: "2026-09-30",
    readingTime: "9 min read",
    cluster: "magnesium",
  },
  {
    slug: "inositol-vs-berberine-insulin-resistance-pmos",
    title: "Inositol vs Berberine for Insulin Resistance in PCOS / PMOS",
    excerpt: "How each works, what the trials show, side effects and whether to combine them.",
    category: "Hormonal Health",
    date: "2026-09-28",
    readingTime: "8 min read",
    cluster: "hormonal",
  },
  {
    slug: "pcod-vs-pcos-vs-pmos",
    title: "PCOS is now PMOS and it is more than an ovarian condition",
    excerpt: "Understand the 2026 name change, PCOD vs PCOS, and why care extends beyond your cycle.",
    category: "Hormonal Health",
    date: "2026-09-21",
    readingTime: "8 min read",
    cluster: "hormonal",
  },
  {
    slug: "inositol-and-hair",
    title: "Inositol and Hair: Can It Help With PCOS Hair Loss and Excess Hair Growth?",
    excerpt:
      "Does inositol fix PCOS hair loss or just hirsutism? The real evidence, the myths, and a realistic 3-6 month timeline.",
    category: "Hormonal Health",
    date: "2026-09-23",
    readingTime: "10 min read",
    cluster: "hormonal",
  },
  {
    slug: "liver-health-supplements-india",
    title: "Liver Health Supplements: What Actually Helps",
    excerpt:
      "The actives with real evidence behind them, how to read an Indian label in ninety seconds, and where everyday nutrition does more than any capsule.",
    category: "Liver & Gut",
    date: "2026-09-17",
    readingTime: "10 min read",
    cluster: "liver-gut",
  },
  {
    slug: "7-day-pcos-diet-plan-india",
    title: "7-Day PCOS Diet Plan (India): What to Eat Each Day",
    excerpt:
      "A full week of real Indian meals built on insulin-friendly eating — the plate method, the day-by-day chart, and the foods worth limiting.",
    category: "Hormonal Health",
    date: "2026-09-18",
    readingTime: "10 min read",
    cluster: "hormonal",
  },
  {
    slug: "best-probiotic-for-gut-health-india",
    title: "Best Probiotic for Gut Health and Digestion",
    excerpt:
      "Why strain names beat CFU counts, how long a probiotic really takes to work, and where curd and fermented food fit alongside a capsule.",
    category: "Liver & Gut",
    date: "2026-09-17",
    readingTime: "9 min read",
    cluster: "liver-gut",
  },
  {
    slug: "how-to-take-inositol-for-pcos-india",
    title: "Inositol for PCOS: How Much, When and How Long",
    excerpt:
      "The 40:1 myo to D-chiro ratio explained simply, the daily amount used in research, and a realistic three-month timeline.",
    category: "Hormonal Health",
    date: "2026-09-17",
    readingTime: "8 min read",
    cluster: "hormonal",
  },
  {
    slug: "gut-liver-axis",
    title: "The Gut-Liver Axis: How Your Gut Controls Your Liver Health",
    excerpt:
      "Your liver processes 70% of its blood from the gut. Here's how dysbiosis drives fatty liver, inflammation and toxin overload, and the evidence-based protocol.",
    category: "Liver & Gut",
    date: "2026-06-08",
    readingTime: "11 min read",
    cluster: "liver-gut",
  },
  {
    slug: "magnesium-benefits",
    title: "Magnesium Benefits: Forms, Foods & Dosage Guide",
    excerpt:
      "What magnesium actually does sleep, recovery, energy, mood plus the five forms in Mag Absorb Pro and how they compare on bioavailability.",
    category: "Minerals",
    date: "2026-06-11",
    readingTime: "10 min read",
    cluster: "magnesium",
  },
  {
    slug: "best-magnesium-supplement-for-sleep",
    title: "Best Magnesium Supplement for Sleep: A Complete Guide",
    excerpt:
      "How magnesium supports sleep quality, the best forms to look for, optimal timing and how to choose a research-backed magnesium supplement.",
    category: "Sleep",
    date: "2026-06-25",
    readingTime: "12 min read",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-glycinate-vs-citrate-india",
    title: "Magnesium Bisglycinate vs Citrate vs Chloride: India Buyer's Guide",
    excerpt:
      "Which magnesium form is best in India bisglycinate, citrate, chloride, oxide or aspartate and how to read a real supplement label.",
    category: "Minerals",
    date: "2026-06-28",
    readingTime: "9 min read",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-deficiency-symptoms-india",
    title: "9 Signs of Magnesium Deficiency Indians Commonly Miss",
    excerpt:
      "Fatigue, cramps, poor sleep, palpitations, migraines, nine everyday signs of low magnesium in Indian adults, and how to fix it.",
    category: "Minerals",
    date: "2026-07-01",
    readingTime: "8 min read",
    cluster: "magnesium",
  },
  {
    slug: "magnesium-rich-foods-india",
    title: "15 Magnesium-Rich Foods in the Indian Diet",
    excerpt:
      "Pumpkin seeds, palak, ragi, rajma and more, foods high in magnesium with per-serving amounts and a realistic daily plan.",
    category: "Minerals",
    date: "2026-08-02",
    readingTime: "10 min read",
    cluster: "magnesium",
  },
  {
    slug: "natural-remedies-sleep-anxiety-india",
    title: "Natural Ways to Deal With Sleep Anxiety (India Guide)",
    excerpt:
      "Racing thoughts at 2 AM? An evidence-based India guide to natural ways to calm the nervous system before bed.",
    category: "Sleep",
    date: "2026-07-07",
    readingTime: "10 min read",
    cluster: "magnesium",
  },
  {
    slug: "where-to-buy-inositol-for-pcos-supplements-online-india",
    title: "Where to Buy Inositol Supplements Online in India",
    excerpt:
      "How to read an inositol label before you order: the ratio, the amount per sachet, the other actives and the cost per day.",
    category: "Hormonal Health",
    date: "2026-09-14",
    readingTime: "7 min read",
    cluster: "hormonal",
  },
  {
    slug: "shareer-ki-thakaan-dukaandaar-magnesium",
    title: "Shareer Ki Thakaan: Dukandaar Ke Liye Guide",
    excerpt:
      "Dukaan ke baad shareer ki thakaan aur pair mein dard? Karan, daily routine aur magnesium ka role samjhein.",
    category: "Sleep",
    date: "2026-09-07",
    readingTime: "9 min read",
    cluster: "magnesium",
  },
  {
    slug: "leg-cramps-at-night-causes-india",
    title: "Leg Cramps at Night: Causes, Fixes and Red Flags",
    excerpt:
      "The 6 real causes of nocturnal calf cramps in Indian adults, and the 3-week protocol that stops them.",
    category: "Sleep",
    date: "2026-07-04",
    readingTime: "8 min read",
    cluster: "magnesium",
  },
  {
    slug: "inositol-pcos",
    title: "Inositol for PCOS: The 40:1 Myo:D-Chiro Ratio Explained",
    excerpt:
      "Why the gold-standard 40:1 ratio outperforms metformin in many PCOS trials, and what to look for in a research-backed sachet.",
    category: "Hormonal Health",
    date: "2026-06-20",
    readingTime: "11 min read",
    cluster: "hormonal",
  },
  {
    slug: "liver-detox-truth",
    title: "Liver Detox: Myths vs Evidence. What Actually Works",
    excerpt:
      "The juice-cleanse industry is built on a myth. Here's what the liver actually does and which ingredients are research-backed.",
    category: "Liver & Gut",
    date: "2026-06-14",
    readingTime: "10 min read",
    cluster: "liver-gut",
  },
  {
    slug: "sluggish-liver-signs-and-fix",
    title: "Signs of a Sluggish Liver and How to Fix It",
    excerpt:
      "Ten early signs a hardworking Indian liver is falling behind, and the 90-day protocol that reverses it.",
    category: "Liver & Gut",
    date: "2026-07-10",
    readingTime: "10 min read",
    cluster: "liver-gut",
  },
  {
    slug: "fatty-liver-diet-plan-india",
    title: "Fatty Liver (NAFLD) Diet Plan. Indian Foods That Help and Hurt",
    excerpt:
      "A practical Indian NAFLD diet plan, what to eat, what to cut, and a realistic Indian day, grounded in the research.",
    category: "Liver & Gut",
    date: "2026-07-12",
    readingTime: "10 min read",
    cluster: "liver-gut",
  },
  {
    slug: "bloating-after-meals-causes-india",
    title: "Bloating After Every Meal? Root Causes + a 4-Week Reset",
    excerpt:
      "The 6 root causes of post-meal bloating in Indian adults and the 4-week reset that actually works.",
    category: "Liver & Gut",
    date: "2026-07-15",
    readingTime: "9 min read",
    cluster: "liver-gut",
  },
  {
    slug: "berberine-guide",
    title: "Berberine: Benefits, Dosage & Evidence",
    excerpt:
      "Berberine activates AMPK like metformin and exercise. Blood sugar, lipids, fatty liver, PCOS, what the trials show and how to dose it.",
    category: "Metabolic Health",
    date: "2026-06-22",
    readingTime: "12 min read",
    cluster: "hormonal",
  },
  {
    slug: "curcumin-bioavailability",
    title: "Curcumin Bioavailability: Liposomal vs Piperine vs Standard",
    excerpt:
      "Standard curcumin has very low oral bioavailability. Here's how liposomal delivery and the Bio-Absorb Matrix blend support absorption.",
    category: "Absorption Science",
    date: "2026-06-17",
    readingTime: "9 min read",
    cluster: "liver-gut",
  },
  {
    slug: "pcos-inositol-guide-india",
    title: "How to Manage PCOS Naturally With Inositol. India Guide",
    excerpt:
      "Dose, timing, myo vs D-chiro, what to expect at 3 and 6 months, and where diet, sleep and berberine fit in.",
    category: "Hormonal Health",
    date: "2026-07-21",
    readingTime: "10 min read",
    cluster: "hormonal",
  },
  {
    slug: "liver-pcos-connection-india",
    title: "The Liver–PCOS Connection: Fatty Liver, Estrogen Clearance & Why Inositol Alone Stalls",
    excerpt:
      "Insulin resistance drives both PCOS and fatty liver. How sluggish clearance feeds hormonal acne and PCOS fatigue, and where inositol, berberine and magnesium fit.",
    category: "Hormonal Health",
    date: "2026-09-25",
    readingTime: "12 min read",
    cluster: "hormonal",
  },
  {
    slug: "irregular-periods-pcos-checklist",
    title: "Irregular Periods & PCOS: A 7-Point Checklist Before You Panic",
    excerpt:
      "The 7-point workup that separates PCOS from thyroid, prolactin, stress and post-pill cycles.",
    category: "Hormonal Health",
    date: "2026-07-18",
    readingTime: "9 min read",
    cluster: "hormonal",
  },
  {
    slug: "berberine-vs-metformin-india",
    title: "Berberine vs Metformin: What the Trials Actually Show",
    excerpt:
      "A head-to-head of the human trials, when berberine is enough, when metformin wins, and how they combine.",
    category: "Metabolic Health",
    date: "2026-07-24",
    readingTime: "10 min read",
    cluster: "hormonal",
  },
  {
    slug: "pcos-metabolic-health-prediabetes",
    title: "PCOS Is More Than an Irregular Period",
    excerpt:
      "Even with normal glucose, PCOS was linked to a 23% higher relative hazard of progressing to prediabetes. What that means and how screening is recommended.",
    category: "Hormonal Health",
    date: "2026-08-28",
    readingTime: "9 min read",
    cluster: "hormonal",
  },
  {
    slug: "why-is-my-period-late",
    title: "Why Is My Period Late? Causes, Tests & When to See a Doctor",
    excerpt:
      "Pregnancy, stress, PCOS, thyroid problems, weight changes, over-exercise, contraception and perimenopause, plus when a delayed period needs investigating.",
    category: "Hormonal Health",
    date: "2026-08-24",
    readingTime: "12 min read",
    cluster: "hormonal",
  },
  {
    slug: "pcos-explained-symptoms-diagnosis-india",
    title: "PCOS Explained: Symptoms, Diagnosis and What to Do Next",
    excerpt:
      "A calm India-focused guide to PCOS symptoms, diagnosis, PCOD vs PCOS, fertility, diet, and when to speak with a doctor.",
    category: "Hormonal Health",
    date: "2026-08-13",
    readingTime: "12 min read",
    cluster: "hormonal",
  },
  {
    slug: "cant-sleep-at-night-what-to-do-magnesium",
    title: "Can't Sleep at Night? What to Do Tonight and Whether Magnesium Helps",
    excerpt:
      "A calm 2 a.m. reset, the real reasons you keep waking up, and an honest answer on magnesium for sleep \u2014 form, dose, timing and safety.",
    category: "Sleep & Magnesium",
    date: "2026-08-14",
    readingTime: "14 min read",
    cluster: "magnesium",
  },
  {
    slug: "best-liver-detox-supplement-india-guide",
    title: "Best Liver Detox Supplement in India: A Complete Buyer's Guide",
    excerpt:
      "The ingredients backed by research, what to avoid, and how to read a liver supplement label in India before you buy.",
    category: "Liver Health",
    date: "2026-08-13",
    readingTime: "11 min read",
    cluster: "liver-gut",
  },
  {
    slug: "hangover-liver-recovery-fatty-liver",
    title: "From Hangover to Fatty Liver: What Your Liver Actually Needs After Alcohol and Processed Food",
    excerpt:
      "Honest guide to hangovers, liver recovery after drinking, fatty liver reversal, and what NAC, silymarin and probiotics can and cannot do.",
    category: "Liver Health",
    date: "2026-08-14",
    readingTime: "18 min read",
    cluster: "liver-gut",
  },
  {
    slug: "fatty-liver-insulin-resistance-diabetes",
    title: "Fatty Liver and Diabetes: How Liver Fat and Insulin Resistance Are Connected",
    excerpt:
      "Why fatty liver, insulin resistance, prediabetes and type 2 diabetes travel together, and the daily habits that support metabolic health.",
    category: "Liver Health",
    date: "2026-08-21",
    readingTime: "12 min read",
    cluster: "liver-gut",
  },
];


export const Route = createFileRoute("/blog/")({
  // CMS posts published from the admin panel are merged into the hand-built
  // library. Failure is swallowed server-side so the hub never blanks out.
  loader: () => listCmsPosts(),
  component: BlogIndex,
  head: () => ({
    meta: [
      { title: "TruWe Clinical Library. Evidence-Based Wellness Guides" },
      {
        name: "description",
        content:
          "Evidence-based wellness guides, magnesium, inositol, berberine, curcumin, liver health and Bio-Absorb Matrix delivery science.",
      },
      { property: "og:title", content: "TruWe Clinical Library" },
      {
        property: "og:description",
        content:
          "Evidence-based wellness guides, magnesium, inositol, berberine, curcumin and Bio-Absorb Matrix bioavailability, reviewed by our science team.",
      },
      { property: "og:url", content: "https://truwe.in/blog" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/blog" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "TruWe Clinical Library",
          url: "https://truwe.in/blog",
          description:
            "Evidence-based wellness guides written and reviewed by the TruWe Science Team.",
          publisher: { "@id": "https://truwe.in/#organization" },
          blogPost: POSTS.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `https://truwe.in/blog/${p.slug}`,
            datePublished: p.date,
            author: { "@type": "Organization", name: "TruWe Clinical Library" },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://truwe.in/blog" },
          ],
        }),
      },
    ],
  }),
});

function toPost(p: PublicPost): Post {
  return {
    slug: p.slug,
    title: p.title,
    excerpt: p.excerpt,
    category: p.category,
    date: p.publishedAt.slice(0, 10),
    readingTime: p.readingTime,
    cluster: (p.cluster ?? "") as ClusterKey,
  };
}

function PostCard({ post: p, index }: { post: Post; index: number }) {
  const t = useT();
  return (
    <Reveal delay={0.04 * index}>
      <a
        href={`/blog/${p.slug}`}
        className="group flex h-full flex-col rounded-3xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-brand/30 hover:shadow-luxury md:p-7"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
          {p.category}
        </p>
        <h3 className="mt-2 font-display text-xl font-bold leading-snug text-brand-deep group-hover:text-brand md:text-2xl">
          {p.title}
        </h3>
        <p className="mt-3 flex-1 text-sm text-muted-foreground">{p.excerpt}</p>
        <div className="mt-5 flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <time dateTime={p.date}>
              {new Date(p.date).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </time>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> {p.readingTime}
            </span>
          </div>
          <span className="inline-flex items-center gap-1 font-semibold text-brand">
            {t("blogc.read")} <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </a>
    </Reveal>
  );
}

function BlogIndex() {
  const t = useT();
  const cmsPosts = Route.useLoaderData() as PublicPost[];
  const clusterKeys = new Set(Object.keys(CLUSTERS));
  const allPosts: Post[] = [...POSTS, ...cmsPosts.map(toPost)].sort((a, b) =>
    a.date < b.date ? 1 : -1,
  );
  const uncategorised = allPosts.filter((p) => !clusterKeys.has(p.cluster));

  return (
    <div className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-4xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            <Beaker className="mr-1.5 inline h-3.5 w-3.5" />
            {t("blogc.eyebrow")}
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            {t("blogc.heading")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">
            {t("blogc.intro")}
          </p>
        </Reveal>

        {/* Cluster hub strip, links straight to the three product hubs + their
            ingredient sections. Turns /blog into a proper hub page. */}
        <Reveal delay={0.05}>
          <nav aria-label={t("blogc.exploreByTopic")} className="mt-10 grid gap-3 md:grid-cols-3">
            {(Object.values(CLUSTERS)).map((c) => (
              <div
                key={c.key}
                className="flex flex-col rounded-2xl border border-border bg-card p-5"
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
                  {c.label}
                </p>
                <div className="mt-3 flex flex-col gap-2 text-sm">
                  <Link
                    to="/products/$handle"
                    params={{ handle: c.productHandle }}
                    className="inline-flex items-center gap-1.5 font-semibold text-brand-deep hover:text-brand"
                  >
                    <Package className="h-3.5 w-3.5 text-brand" />
                    {c.productTitle}
                    <ArrowRight className="ml-auto h-3.5 w-3.5 text-brand" />
                  </Link>
                  <a
                    href={`/ingredients#${c.ingredientAnchor}`}
                    className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-brand-deep"
                  >
                    <FlaskConical className="h-3.5 w-3.5 text-brand" />
                    {c.ingredientLabel}
                  </a>
                </div>
              </div>
            ))}
          </nav>
        </Reveal>

        {/* Articles grouped by cluster (was flat list). Each cluster gets its
            own heading so search engines can see the topical grouping. */}
        {(Object.values(CLUSTERS)).map((c) => {
          const posts = allPosts.filter((p) => p.cluster === c.key);
          if (!posts.length) return null;
          return (
            <section key={c.key} className="mt-14">
              <div className="flex items-end justify-between gap-3">
                <h2 className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
                  {c.label}
                </h2>
                <Link
                  to="/products/$handle"
                  params={{ handle: c.productHandle }}
                  className="text-xs font-semibold text-brand hover:underline"
                >
                  {t("blogc.shopCta", { product: c.productTitle })}
                </Link>
              </div>
              <div className="mt-6 grid gap-6 md:grid-cols-2">
                {posts.map((p, i) => (
                  <PostCard key={p.slug} post={p} index={i} />
                ))}
              </div>
            </section>
          );
        })}

        {/* CMS posts that aren't assigned to a cluster still need a home. */}
        {uncategorised.length ? (
          <section className="mt-14">
            <h2 className="font-display text-2xl font-bold text-brand-deep md:text-3xl">
              {t("blogc.moreFromLibrary")}
            </h2>
            <div className="mt-6 grid gap-6 md:grid-cols-2">
              {uncategorised.map((p, i) => (
                <PostCard key={p.slug} post={p} index={i} />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}